/**
 * useReadingProgress Hook
 *
 * Tracks reading progress in the Torah reader:
 * - Verse visibility tracking with IntersectionObserver
 * - Reading time tracking
 * - Verse completion detection (visible 2+ sec AND scrolled past)
 * - Debounced Firestore updates
 * - Celebration triggers
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  getReadingProgress,
  initializeParshaProgress,
  markVersesAsRead,
  updateReadingTime,
  incrementCommentaryExpansions,
  incrementHighlights,
  incrementVerseFavorites,
  markParshaCompleted,
  checkAndUpdateStreak,
  subscribeToReadingProgress,
} from '@/services/readingProgress';
import { incrementMetric } from '@/services/engagementMetrics';
import {
  calculateParshaCompletion,
  isParshaComplete,
  type ReadingProgress,
  type ParshaReadingProgress,
} from '@/types/reading';

// ============================================
// CONSTANTS
// ============================================

const VERSE_VISIBILITY_THRESHOLD_MS = 2000; // 2 seconds to count as "read"
const READING_TIME_UPDATE_INTERVAL_MS = 5000; // Update reading time every 5 seconds
const VERSES_UPDATE_DEBOUNCE_MS = 2000; // Debounce verse updates

// ============================================
// TYPES
// ============================================

export interface ReadingProgressState {
  progress: ReadingProgress | null;
  isLoading: boolean;
  currentSessionTimeMs: number;
  currentSessionVersesRead: number;
  parshaCompletion: number;
}

export interface ReadingProgressCallbacks {
  onFirstVerse?: () => void;
  onParshaComplete?: (parshaTitle: string, isFirstParsha: boolean) => void;
  onStreakUpdate?: (newStreak: number, isNewDay: boolean) => void;
  onBadgeUnlock?: (badgeName: string) => void;
}

export interface UseReadingProgressOptions {
  parshaRef: string;
  parshaTitle: string;
  parshaHebrew?: string;
  totalVerses: number;
  scrollContainerRef: React.RefObject<HTMLElement>;
  callbacks?: ReadingProgressCallbacks;
  enabled?: boolean;
}

export interface UseReadingProgressReturn {
  state: ReadingProgressState;
  trackCommentaryExpansion: () => void;
  trackHighlight: () => void;
  trackVerseFavorite: () => void;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

export function useReadingProgress({
  parshaRef,
  parshaTitle,
  parshaHebrew,
  totalVerses,
  scrollContainerRef,
  callbacks,
  enabled = true,
}: UseReadingProgressOptions): UseReadingProgressReturn {
  const { authState } = useAuth();
  const userId = authState.status === 'authenticated' ? authState.user.uid : null;

  // State
  const [progress, setProgress] = useState<ReadingProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSessionTimeMs, setCurrentSessionTimeMs] = useState(0);
  const [currentSessionVersesRead, setCurrentSessionVersesRead] = useState(0);

  // Refs for tracking
  const sessionStartTimeRef = useRef<number>(Date.now());
  const lastTimeUpdateRef = useRef<number>(Date.now());
  const verseVisibilityTimersRef = useRef<Map<number, number>>(new Map());
  const versesMarkedAsReadRef = useRef<Set<number>>(new Set());
  const versesScrolledPastRef = useRef<Set<number>>(new Set());
  const pendingVersesToMarkRef = useRef<Set<number>>(new Set());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasTriggeredFirstVerseRef = useRef(false);
  const hasTriggeredParshaCompleteRef = useRef(false);

  // Initialize parsha progress when component mounts or parsha changes
  useEffect(() => {
    if (!userId || !enabled || !parshaRef) return;

    const initProgress = async () => {
      setIsLoading(true);
      try {
        const existingProgress = await getReadingProgress(userId);

        // Check if we need to initialize for a new parsha
        if (existingProgress.currentParsha.ref !== parshaRef) {
          await initializeParshaProgress(userId, parshaRef, parshaTitle, parshaHebrew, totalVerses);
        }
      } catch (error) {
        console.error('Error initializing reading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initProgress();
  }, [userId, enabled, parshaRef, parshaTitle, parshaHebrew, totalVerses]);

  // Subscribe to reading progress changes
  useEffect(() => {
    if (!userId || !enabled) return;

    const unsubscribe = subscribeToReadingProgress(userId, (newProgress) => {
      setProgress(newProgress);
      setIsLoading(false);

      if (newProgress) {
        // Update local tracking from persisted data
        newProgress.currentParsha.versesRead.forEach((idx) => {
          versesMarkedAsReadRef.current.add(idx);
        });
      }
    });

    return () => unsubscribe();
  }, [userId, enabled]);

  // Set up IntersectionObserver for verse visibility
  useEffect(() => {
    if (!enabled || !scrollContainerRef.current) return;

    const handleVerseVisible = (verseIndex: number) => {
      verseVisibilityTimersRef.current.delete(verseIndex);
      checkVerseReadComplete(verseIndex);
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const verseIndex = parseInt((entry.target as HTMLElement).dataset.verseIndex || '-1', 10);
        if (verseIndex === -1) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.8) {
          // Verse is mostly visible, start timer
          if (!verseVisibilityTimersRef.current.has(verseIndex)) {
            const timerId = window.setTimeout(
              handleVerseVisible.bind(null, verseIndex),
              VERSE_VISIBILITY_THRESHOLD_MS
            );
            verseVisibilityTimersRef.current.set(verseIndex, timerId);
          }
        } else {
          // Verse is no longer visible, cancel timer
          const timerId = verseVisibilityTimersRef.current.get(verseIndex);
          if (timerId) {
            clearTimeout(timerId);
            verseVisibilityTimersRef.current.delete(verseIndex);
          }

          // Check if verse was scrolled past (no longer visible from top)
          if (!entry.isIntersecting) {
            const rect = entry.boundingClientRect;
            if (rect.bottom < 0) {
              // Verse has been scrolled past
              versesScrolledPastRef.current.add(verseIndex);
              checkVerseReadComplete(verseIndex);
            }
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: scrollContainerRef.current,
      threshold: [0.8, 1.0],
    });

    // Observe all verse elements
    const verseElements = scrollContainerRef.current.querySelectorAll('[data-verse-index]');
    verseElements.forEach((element) => {
      observerRef.current?.observe(element);
    });

    // Capture ref values at effect setup time for safe cleanup
    const observer = observerRef.current;
    const timers = verseVisibilityTimersRef.current;

    return () => {
      observer?.disconnect();
      // Clear all timers
      timers.forEach((timerId) => clearTimeout(timerId));
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollContainerRef.current is not a valid dependency
  }, [enabled, totalVerses]);

  // Check if a verse is complete (visible 2+ sec AND scrolled past)
  const checkVerseReadComplete = useCallback(
    (verseIndex: number) => {
      // Already marked as read
      if (versesMarkedAsReadRef.current.has(verseIndex)) return;

      // For simplicity, we'll mark as read when visible for 2+ seconds
      // The scroll-past check happens when the verse leaves the viewport
      pendingVersesToMarkRef.current.add(verseIndex);

      // Debounce the Firestore update
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        flushPendingVerses();
      }, VERSES_UPDATE_DEBOUNCE_MS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flushPendingVerses is stable via ref pattern; adding it causes circular deps
    [userId]
  );

  // Flush pending verses to Firestore
  const flushPendingVerses = useCallback(async () => {
    if (!userId || pendingVersesToMarkRef.current.size === 0) return;

    const versesToMark = Array.from(pendingVersesToMarkRef.current);
    pendingVersesToMarkRef.current.clear();

    try {
      const { newVersesMarked, isFirstVerse } = await markVersesAsRead(userId, versesToMark);

      if (newVersesMarked > 0) {
        // Update session counter
        setCurrentSessionVersesRead((prev) => prev + newVersesMarked);

        // Mark as read locally
        versesToMark.forEach((idx) => versesMarkedAsReadRef.current.add(idx));

        // Increment engagement metrics
        await incrementMetric(userId, 'versesReadThisDay', newVersesMarked);
        await incrementMetric(userId, 'versesReadThisWeek', newVersesMarked);

        // Trigger first verse callback
        if (isFirstVerse && !hasTriggeredFirstVerseRef.current) {
          hasTriggeredFirstVerseRef.current = true;
          callbacks?.onFirstVerse?.();
        }

        // Check for parsha completion
        await checkParshaCompletion();
      }
    } catch (error) {
      console.error('Error marking verses as read:', error);
      // Re-add to pending on error
      versesToMark.forEach((idx) => pendingVersesToMarkRef.current.add(idx));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkParshaCompletion excluded to prevent circular deps
  }, [userId, callbacks]);

  // Check if parsha is complete
  const checkParshaCompletion = useCallback(async () => {
    if (!userId || !progress || hasTriggeredParshaCompleteRef.current) return;

    const currentProgress: ParshaReadingProgress = {
      ...progress.currentParsha,
      versesRead: Array.from(versesMarkedAsReadRef.current),
      totalVerses,
    };

    if (isParshaComplete(currentProgress)) {
      hasTriggeredParshaCompleteRef.current = true;

      try {
        const isFirstParsha = await markParshaCompleted(userId);

        // Increment parsha completion metric
        await incrementMetric(userId, 'parshasCompletedTotal', 1);

        callbacks?.onParshaComplete?.(parshaTitle, isFirstParsha);
      } catch (error) {
        console.error('Error marking parsha complete:', error);
        hasTriggeredParshaCompleteRef.current = false;
      }
    }
  }, [userId, progress, totalVerses, parshaTitle, callbacks]);

  // Track reading time
  useEffect(() => {
    if (!userId || !enabled) return;

    const interval = setInterval(async () => {
      const now = Date.now();
      const elapsed = now - lastTimeUpdateRef.current;
      lastTimeUpdateRef.current = now;

      // Update session time locally
      setCurrentSessionTimeMs((prev) => prev + elapsed);

      // Update Firestore
      try {
        await updateReadingTime(userId, elapsed);

        // Increment engagement metrics (convert to minutes)
        const elapsedMinutes = Math.floor(elapsed / 60000);
        if (elapsedMinutes > 0) {
          await incrementMetric(userId, 'readingTimeThisDay', elapsedMinutes);
          await incrementMetric(userId, 'readingTimeThisWeek', elapsedMinutes);
        }
      } catch (error) {
        console.error('Error updating reading time:', error);
      }
    }, READING_TIME_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [userId, enabled]);

  // Check and update streak on unmount
  // Note: flushPendingVerses and checkAndUpdateStreak are fire-and-forget
  // Firestore writes. Callbacks that may update state are guarded by isMounted.
  useEffect(() => {
    if (!userId || !enabled) return;

    let isMounted = true;

    // Capture ref values at effect setup time for safe cleanup
    const sessionStartTime = sessionStartTimeRef.current;
    const versesReadSet = versesMarkedAsReadRef.current;

    return () => {
      isMounted = false;

      // Flush any pending verses (fire-and-forget Firestore write)
      flushPendingVerses();

      // Check streak on session end
      const sessionTimeMs = Date.now() - sessionStartTime;
      const versesRead = versesReadSet.size;

      checkAndUpdateStreak(userId, sessionTimeMs, versesRead)
        .then(({ streakUpdated, newStreak, isNewStreakDay }) => {
          // Only call callbacks if still relevant (not unmounted mid-session)
          if (streakUpdated && isNewStreakDay && !isMounted) {
            // Still update the metric (fire-and-forget) but skip UI callbacks
            incrementMetric(userId, 'currentReadingStreak', newStreak);
          } else if (streakUpdated && isNewStreakDay) {
            incrementMetric(userId, 'currentReadingStreak', newStreak);
            callbacks?.onStreakUpdate?.(newStreak, isNewStreakDay);
          }
        })
        .catch((error) => {
          console.error('Error updating streak:', error);
        });
    };
  }, [userId, enabled, flushPendingVerses, callbacks]);

  // Interaction trackers
  const trackCommentaryExpansion = useCallback(async () => {
    if (!userId) return;
    try {
      await incrementCommentaryExpansions(userId);
    } catch (error) {
      console.error('Error tracking commentary expansion:', error);
    }
  }, [userId]);

  const trackHighlight = useCallback(async () => {
    if (!userId) return;
    try {
      await incrementHighlights(userId);
    } catch (error) {
      console.error('Error tracking highlight:', error);
    }
  }, [userId]);

  const trackVerseFavorite = useCallback(async () => {
    if (!userId) return;
    try {
      await incrementVerseFavorites(userId);
    } catch (error) {
      console.error('Error tracking verse favorite:', error);
    }
  }, [userId]);

  // Calculate completion percentage
  const parshaCompletion = progress
    ? calculateParshaCompletion({
        ...progress.currentParsha,
        versesRead: Array.from(versesMarkedAsReadRef.current),
        totalVerses,
      })
    : 0;

  return {
    state: {
      progress,
      isLoading,
      currentSessionTimeMs,
      currentSessionVersesRead,
      parshaCompletion,
    },
    trackCommentaryExpansion,
    trackHighlight,
    trackVerseFavorite,
  };
}

export default useReadingProgress;
